import { useEffect, useRef, useState } from "react";
import {
  AreaChart,
  BarChart3,
  Check,
  ChevronDown,
  Circle,
  LineChart,
  PieChart,
} from "lucide-react";

export type ChartType = "bar" | "line" | "area" | "pie" | "donut";
const options = [
  { value: "bar" as const, label: "Bar", icon: BarChart3 },
  { value: "line" as const, label: "Line", icon: LineChart },
  { value: "area" as const, label: "Area", icon: AreaChart },
  { value: "pie" as const, label: "Pie", icon: PieChart },
  { value: "donut" as const, label: "Donut", icon: Circle },
];
export function useChartType(
  key: string,
  initial: ChartType,
  allowed: ChartType[],
) {
  const [type, setType] = useState<ChartType>(() => {
    try {
      const stored = localStorage.getItem(`admin.chart.${key}`) as ChartType;
      return allowed.includes(stored) ? stored : initial;
    } catch {
      return initial;
    }
  });
  const change = (next: ChartType) => {
    if (!allowed.includes(next)) return;
    setType(next);
    try {
      localStorage.setItem(`admin.chart.${key}`, next);
    } catch {
      /* The view still changes when storage is unavailable. */
    }
  };
  return [type, change] as const;
}
export function ChartTypeMenu({
  value,
  onChange,
  allowed,
  label,
}: {
  value: ChartType;
  onChange: (type: ChartType) => void;
  allowed: ChartType[];
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const active = options.find((o) => o.value === value)!;
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        ref={trigger}
        type="button"
        aria-label={`${label} chart type: ${active.label}`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 text-xs font-medium text-text-secondary transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <active.icon size={14} />
        <span>{active.label}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-20 mt-1.5 w-36 rounded-xl border border-border bg-surface-elevated p-1 shadow-elevated"
          aria-label={`${label} chart options`}
        >
          {options
            .filter((o) => allowed.includes(o.value))
            .map((o) => (
              <button
                key={o.value}
                type="button"
                aria-pressed={o.value === value}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                  trigger.current?.focus();
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${o.value === value ? "bg-primary-soft font-semibold text-primary" : "text-text-secondary hover:bg-surface-alt"}`}
              >
                <o.icon size={14} />
                {o.label}
                {o.value === value && <Check className="ml-auto" size={13} />}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
